import { Button, TableCell, TableRow } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import { useHistory } from "react-router-dom";

import useAccountMetadata from "../../../hooks/useAccountMetadata";
import Label from "../../../Label";
import { isAccountInHIPAAMode, isAccountInHIPAAModeV2 } from "../../../util";
import { destinationHasPropertyMetadata } from "../../DestinationPropertyMetadata";

const useStyles = makeStyles(() => ({
  label: {
    width: 120,
    fontSize: "14px",
  },
  configRowDisabled: {
    background: "#FAFAFA",
  },
  configTextDisabled: {
    color: "#CCC",
  },
}));

export function DestinationConfigRow({
  name,
  textColor,
  labelText,
  onOpenConfigModal,
  disabled = false,
  buttonText = "Configure",
  buttonComponent = null,
}) {
  const classes = useStyles();

  return (
    <TableRow className={clsx(disabled ? classes.configRowDisabled : "")}>
      <TableCell>
        <h5
          className={clsx(
            "text-md font-medium text-brand-navy",
            disabled ? classes.configTextDisabled : ""
          )}
        >
          {name}
        </h5>
      </TableCell>
      <TableCell align="center">
        {!disabled && labelText && (
          <Label
            variant="no-background"
            style={{
              textTransform: "none",
              justifyContent: "left",
              color: textColor,
            }}
            className={clsx(classes.label)}
          >
            {labelText}
          </Label>
        )}
      </TableCell>
      <TableCell align="right">
        {!disabled &&
          (buttonComponent ? (
            buttonComponent
          ) : (
            <Button
              onClick={onOpenConfigModal}
              color="primary"
              variant="outlined"
            >
              {buttonText}
            </Button>
          ))}
      </TableCell>
    </TableRow>
  );
}

DestinationConfigRow.propTypes = {
  name: PropTypes.string.isRequired,
  labelColor: PropTypes.string,
  labelText: PropTypes.string,
  onOpenConfigModal: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  buttonText: PropTypes.string,
  buttonComponent: PropTypes.element,
};

export function DestinationHIPAARow({
  onOpenConfigModal,
  disabled = false,
  buttonText = "Configure",
  buttonComponent = null,
  configuredDestination,
}) {
  const accountMetadata = useAccountMetadata();
  const classes = useStyles();

  if (!isAccountInHIPAAMode(accountMetadata)) {
    return null;
  }

  return (
    <TableRow className={clsx(disabled ? classes.configRowDisabled : "")}>
      <TableCell>
        <h5
          className={clsx(
            "text-md font-medium text-brand-navy",
            disabled ? classes.configTextDisabled : ""
          )}
        >
          HIPAA Settings
        </h5>
      </TableCell>
      <TableCell align="center">
        <Label
          style={{ textTransform: "none", justifyContent: "left" }}
          variant="no-background"
          className={clsx(classes.label)}
        >
          {configuredDestination.has_baa ? "Allowing PHI" : "Restricting PHI"}
        </Label>
      </TableCell>
      <TableCell align="right">
        {!disabled &&
          (buttonComponent ? (
            buttonComponent
          ) : (
            <Button
              onClick={onOpenConfigModal}
              color="primary"
              variant="outlined"
            >
              {buttonText}
            </Button>
          ))}
      </TableCell>
    </TableRow>
  );
}

DestinationHIPAARow.propTypes = {
  onOpenConfigModal: PropTypes.func.isRequired,
  configuredDestination: PropTypes.object.isRequired,
};

export function DestinationPHIAllowlistConfigRow({
  destination,
  configuredDestination,
  disabled,
}) {
  const accountMetadata = useAccountMetadata();
  const history = useHistory();

  const onConfigure = () => {
    history.push(destination.configPageUrl() + "/phiallowlist");
  };

  if (!isAccountInHIPAAModeV2(accountMetadata)) {
    return null;
  }

  if (!destination.supportsServerSide) {
    return null;
  }

  let rowLabel = "HIPAA Allow List";
  if (destinationHasPropertyMetadata(destination.key)) {
    rowLabel = "Advanced: " + rowLabel;
  }

  return (
    <DestinationConfigRow
      name={rowLabel}
      onOpenConfigModal={onConfigure}
      buttonText="Configure"
      disabled={disabled || configuredDestination.has_baa}
    />
  );
}

DestinationPHIAllowlistConfigRow.propTypes = {
  destination: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

export function DestinationConnectionModeRow({
  onOpenConfigModal,
  disabled = false,
  buttonText = "Configure",
  buttonComponent = null,
  configuredDestination,
}) {
  const classes = useStyles();

  let connectionModeLabel = "None specified";

  if (configuredDestination.connection_mode === "client") {
    connectionModeLabel = "Client-side";
  } else if (configuredDestination.connection_mode === "server") {
    connectionModeLabel = "Server-side";
  }

  return (
    <TableRow className={clsx(disabled ? classes.configRowDisabled : "")}>
      <TableCell>
        <h5
          className={clsx(
            "text-md font-medium text-brand-navy",
            disabled ? classes.configTextDisabled : ""
          )}
        >
          Connection Mode Settings
        </h5>
      </TableCell>
      <TableCell align="center">
        <Label
          style={{ textTransform: "none", justifyContent: "left" }}
          variant="no-background"
          className={clsx(classes.label)}
        >
          {connectionModeLabel}
        </Label>
      </TableCell>
      <TableCell align="right">
        {!disabled &&
          (buttonComponent ? (
            buttonComponent
          ) : (
            <Button
              onClick={onOpenConfigModal}
              color="primary"
              variant="outlined"
            >
              {buttonText}
            </Button>
          ))}
      </TableCell>
    </TableRow>
  );
}

DestinationConnectionModeRow.propTypes = {
  onOpenConfigModal: PropTypes.func.isRequired,
  configuredDestination: PropTypes.object.isRequired,
};
